import {announce} from '@github-ui/aria-live'
import {TaillightsProvider} from '@github-ui/taillights/provider'
import type {SourceDocument} from '@github-ui/taillights-worker/document'
import {type ReactNode, useCallback, useMemo, useState} from 'react'

import {SourceContext, StartupErrorContext} from './source-context'

export interface CodeBlobTaillightsProviderProps {
  /** Null keeps the connection idle while source data is pending. */
  source: SourceDocument | null
  tabSize?: number
  appearanceKey?: string
  children: ReactNode
}

/**
 * Owns the single source/profile connection. Sibling consumers can use
 * useTaillightsController from @github-ui/taillights/provider to share it.
 */
export function CodeBlobTaillightsProvider({
  source,
  tabSize = 4,
  appearanceKey,
  children,
}: CodeBlobTaillightsProviderProps) {
  const rendering = useMemo(() => ({tabSize}), [tabSize])
  const configuration = useMemo(() => ({source, rendering, appearanceKey}), [source, rendering, appearanceKey])
  const [scope, setScope] = useState({source, revision: 0})
  if (scope.source !== source) {
    setScope({source, revision: scope.revision + (scope.source !== null && source !== null ? 1 : 0)})
  }
  const [failure, setFailure] = useState<{
    configuration: typeof configuration
    message: string
  } | null>(null)
  const onError = useCallback(
    (error: Error) => {
      if (error.name !== 'AbortError') reportError(error)
      setFailure({configuration, message: error.message})
      announce('Source preparation failed. Original source text remains available.')
    },
    [configuration],
  )
  const error = failure?.configuration === configuration ? failure.message : ''

  return (
    <TaillightsProvider source={source} rendering={rendering} appearanceKey={appearanceKey} onError={onError}>
      <SourceContext value={source}>
        <StartupErrorContext key={scope.revision} value={error}>
          {children}
        </StartupErrorContext>
      </SourceContext>
    </TaillightsProvider>
  )
}
